/**
 * Post-upgrade security choice + SMS 2FA (letter-first Phase 8).
 *
 * Authenticated calls ride the shared axios client; the pre-auth login
 * challenge pair uses plain fetch because it runs before a token exists.
 */
import { API_URL } from '../config';
import { createLogger } from '../utils/logger';

import http from './http';

const logger = createLogger('securityService');

// Axios throws on non-2xx before our success checks run, hiding the
// server's actual message ("That code has expired — request a new one")
// behind "Request failed with status code 400". Unwrap it.
function serverError(error: any, fallback: string): Error {
  return new Error(error?.response?.data?.message || error?.message || fallback);
}

export interface SecurityPreference {
  decision: 'totp' | 'sms' | 'both' | 'declined' | null;
  totpEnabled: boolean;
  sms2faEnabled: boolean;
  sms2faPhoneMasked: string | null;
  smsAvailable: boolean;
}

class SecurityServiceApi {
  async getPreference(): Promise<SecurityPreference> {
    const response = await http.get('/api/security/preference');
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to load security preference');
    }
    return response.data.data;
  }

  async recordDecision(decision: 'totp' | 'sms' | 'both' | 'declined'): Promise<void> {
    const response = await http.post('/api/security/preference', { decision });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to save your choice');
    }
  }

  async startSmsEnrollment(phone: string): Promise<{ phoneMasked: string }> {
    try {
      const response = await http.post('/api/security/sms-2fa/start', { phone });
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to send the code');
      }
      return response.data.data;
    } catch (error) {
      throw serverError(error, 'Failed to send the code');
    }
  }

  async confirmSmsEnrollment(code: string): Promise<{ phoneMasked: string | null }> {
    try {
      const response = await http.post('/api/security/sms-2fa/confirm', { code });
      if (!response.data.success) {
        throw new Error(response.data.message || 'Invalid code');
      }
      return response.data.data;
    } catch (error) {
      throw serverError(error, 'Invalid code');
    }
  }

  async disableSms2fa(): Promise<void> {
    try {
      const response = await http.post('/api/security/sms-2fa/disable');
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to turn off text codes');
      }
    } catch (error) {
      throw serverError(error, 'Failed to turn off text codes');
    }
  }

  /**
   * Pre-auth: ask the server to text a login code. Fire-and-report. The
   * server texts only with the password step's ticket (2026-09-24).
   */
  async sendLoginCode(params: { userId: string; secondFactorTicket?: string }): Promise<boolean> {
    try {
      const response = await fetch(`${API_URL}/api/security/sms-2fa/login-send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: params.userId, secondFactorTicket: params.secondFactorTicket }),
      });
      return response.ok;
    } catch (error) {
      logger.error('Login code send failed:', error);
      return false;
    }
  }

  /** Pre-auth: verify the code; returns the token payload on success. */
  async verifyLoginCode(params: {
    userId: string;
    code: string;
    /** The password step's ticket (2026-09-23). */
    secondFactorTicket?: string;
    rememberDevice?: boolean;
    deviceInfo?: Record<string, unknown>;
  }): Promise<any> {
    const response = await fetch(`${API_URL}/api/security/sms-2fa/login-verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data?.message || 'Invalid verification code');
    }
    return data;
  }
}

export const securityService = new SecurityServiceApi();
